import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button } from "../../../../ui/components/Button";
import { FeatherLoader, FeatherDownload } from "@subframe/core";
import {
  ParsedExcelData,
  ExcelCell,
  ExcelSheet,
  CellBorders,
  parseReportForViewer,
  downloadReport,
  getLedgerTransactions,
  getStoredCompanyId,
  TrialBalanceTransaction,
} from "../../data/api/trialBalanceApi";
import {
  cellStyleToCSS,
  formatWithNumberFormat,
  parseMergedCells,
  MergedCellInfo,
} from "../utils/excelStyleUtils";
import {
  getAllSheetReferences,
  parseTrialBalanceCriteria,
  TrialBalanceCriteria,
} from "../utils/formulaParser";

const parsedReportCache = new Map<number, ParsedExcelData>();

export const clearParsedReportCache = (): void => {
  parsedReportCache.clear();
};

const columnToIndex = (col: string): number => {
  let index = 0;
  for (let i = 0; i < col.length; i++) {
    index = index * 26 + (col.charCodeAt(i) - 64);
  }
  return index;
};

const indexToColumn = (index: number): string => {
  let result = "";
  while (index > 0) {
    const remainder = (index - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    index = Math.floor((index - 1) / 26);
  }
  return result;
};

const parseAddress = (cellAddress: string): { col: string; row: number } | null => {
  const match = cellAddress.match(/^([A-Z]+)(\d+)$/);
  if (!match) return null;
  return { col: match[1], row: Number(match[2]) };
};

const borderToCss = (
  borders: CellBorders | undefined,
  side: "top" | "bottom" | "left" | "right"
): string | undefined => {
  const border = borders?.[side];
  if (!border) return undefined;
  return `${border.style} ${border.color}`;
};

/** Get border CSS from any cell in the row for the given side (so row borders extend across Notes etc.) */
const getRowBorderForSide = (
  row: ExcelCell[],
  side: "top" | "bottom"
): string | undefined => {
  for (const c of row) {
    const css = borderToCss(c.style?.borders, side);
    if (css) return css;
  }
  return undefined;
};

export interface InlineExcelViewerProps {
  reportId: number | null;
  reportName: string;
  targetSheet: string; // "IS" or "BS"
  onDownload?: () => void;
  fromDate?: string; // For register drill-down
  toDate?: string; // For register drill-down
  headerActions?: React.ReactNode;
  showHeaderWhenEmpty?: boolean;
  fallbackTitle?: string;
  /** When set, header shows "Title — As of …" instead of the report name date range (Apply range unchanged). */
  headerAsOfLabel?: string;
  isGenerating?: boolean;
  emptyState?: React.ReactNode;
  showStatusBar?: boolean;
}

interface SelectedCell {
  sheet: string;
  row: number;
  col: string;
  value: string | number | null;
  formula: string | null;
}

interface BreadcrumbItem {
  sheet: string;
  cell: string;
}

interface TrialBalanceDrilldownState {
  isOpen: boolean;
  ledgerName: string;
  class1?: string;
  year: number;
  monthNumber: number;
  headerLabel: string;
}

export function InlineExcelViewer({
  reportId,
  reportName,
  targetSheet,
  onDownload,
  fromDate,
  toDate,
  headerActions,
  showHeaderWhenEmpty = false,
  fallbackTitle,
  headerAsOfLabel,
  isGenerating = false,
  emptyState,
  showStatusBar = true,
}: InlineExcelViewerProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [excelData, setExcelData] = useState<ParsedExcelData | null>(null);
  const [activeSheet, setActiveSheet] = useState<string>("");
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [targetCellAddress, setTargetCellAddress] = useState<string | null>(null);
  const [trialBalanceModal, setTrialBalanceModal] = useState<TrialBalanceDrilldownState | null>(null);
  const [trialBalanceLoading, setTrialBalanceLoading] = useState<boolean>(false);
  const [trialBalanceError, setTrialBalanceError] = useState<string>("");
  const [trialBalanceTransactions, setTrialBalanceTransactions] = useState<TrialBalanceTransaction[]>([]);
  const [trialBalanceOpeningBalance, setTrialBalanceOpeningBalance] = useState<number | null>(null);
  const [trialBalanceClosingBalance, setTrialBalanceClosingBalance] = useState<number | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);

  const setSheetFromData = useCallback(
    (data: ParsedExcelData) => {
      const targetSheetName = data.sheetNames.find(
        (name) => name.toUpperCase() === targetSheet.toUpperCase()
      );

      if (targetSheetName) {
        setActiveSheet(targetSheetName);
      } else if (data.sheetNames.length > 0) {
        setActiveSheet(data.sheetNames[0]);
      }
    },
    [targetSheet]
  );

  // Load Excel data when reportId changes
  useEffect(() => {
    if (reportId) {
      loadExcelData();
    } else {
      setExcelData(null);
      setActiveSheet("");
      setSelectedCell(null);
      setBreadcrumbs([]);
      setTargetCellAddress(null);
    }
  }, [reportId]);

  const loadExcelData = async () => {
    if (!reportId) return;

    const cachedData = parsedReportCache.get(reportId);
    if (cachedData) {
      setExcelData(cachedData);
      setSheetFromData(cachedData);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await parseReportForViewer(reportId);
      setExcelData(response.data);
      parsedReportCache.set(reportId, response.data);
      setSheetFromData(response.data);
    } catch (err: any) {
      console.error("Failed to load Excel data:", err);
      setError(err.message || "Failed to load Excel data");
    } finally {
      setLoading(false);
    }
  };

  // Get the current sheet data
  const currentSheet: ExcelSheet | null = excelData?.sheets[activeSheet] || null;

  // Filter and process sheet data for display (apply to all sheets)
  const filteredSheet = useMemo((): ExcelSheet | null => {
    if (!currentSheet) return null;

    // Find the row index where "As per Trial Balance" appears in column A
    let cutoffRowIndex = currentSheet.rows.length;
    for (let i = 0; i < currentSheet.rows.length; i++) {
      const row = currentSheet.rows[i];
      const cellA = row.find((cell) => cell.col === "A");
      if (cellA && String(cellA.value || "").trim().toLowerCase().includes("as per trial balance")) {
        cutoffRowIndex = i;
        break;
      }
    }

    // Filter rows:
    // 1. Skip rows 1 and 2 (indices 0 and 1)
    // 2. Remove "As per Trial Balance" and below
    // 3. Remove column A from each row
    const filteredRows = currentSheet.rows
      .slice(2, cutoffRowIndex) // Skip first 2 rows, stop at cutoff
      .map((row) => row.filter((cell) => cell.col !== "A")); // Remove column A

    return {
      ...currentSheet,
      rows: filteredRows,
    };
  }, [currentSheet]);

  // Parse merged cells into lookup map
  const mergedCellsMap = useMemo((): Map<string, MergedCellInfo> => {
    return parseMergedCells(currentSheet?.mergedCells);
  }, [currentSheet?.mergedCells]);

  const filteredCellMap = useMemo((): Map<string, ExcelCell> => {
    const map = new Map<string, ExcelCell>();
    if (!filteredSheet) {
      return map;
    }
    for (const row of filteredSheet.rows) {
      for (const cell of row) {
        map.set(`${cell.col}${cell.row}`, cell);
      }
    }
    return map;
  }, [filteredSheet]);

  const findHeaderRow = (): ExcelCell[] | null => {
    if (!currentSheet) return null;
    const headerRegex =
      /(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s*[- ]\s*\d{4}/i;
    for (const row of currentSheet.rows) {
      const headerCells = row.filter(
        (cell) => cell.value && headerRegex.test(String(cell.value))
      );
      if (headerCells.length >= 2) {
        return row;
      }
    }
    return null;
  };

  const getHeaderValueForColumn = (column: string): string | null => {
    if (!currentSheet) return null;
    const headerRow = findHeaderRow() || currentSheet.rows[0];
    if (!headerRow) return null;
    const headerCell = headerRow.find((cell) => cell.col === column);
    if (!headerCell?.value) {
      return null;
    }
    return String(headerCell.value).trim();
  };

  const parseMonthYearFromHeader = (
    header: string
  ): { year: number; monthNumber: number } | null => {
    const normalized = header.trim().toLowerCase();
    const match = normalized.match(
      /(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s*[- ]\s*(\d{4})/
    );
    if (!match) {
      return null;
    }

    const monthKey = match[1];
    const year = Number(match[2]);
    const monthMap: Record<string, number> = {
      jan: 1,
      january: 1,
      feb: 2,
      february: 2,
      mar: 3,
      march: 3,
      apr: 4,
      april: 4,
      may: 5,
      jun: 6,
      june: 6,
      jul: 7,
      july: 7,
      aug: 8,
      august: 8,
      sep: 9,
      september: 9,
      oct: 10,
      october: 10,
      nov: 11,
      november: 11,
      dec: 12,
      december: 12,
    };

    const monthNumber = monthMap[monthKey];
    if (!monthNumber || !year) {
      return null;
    }

    return { year, monthNumber };
  };

  const handleTrialBalanceDrilldown = async (
    cell: ExcelCell,
    criteria: TrialBalanceCriteria
  ) => {
    const ledgerName = criteria.l2?.trim();
    console.debug("[TB Drilldown] Click", {
      sheet: activeSheet,
      cell: `${cell.col}${cell.row}`,
      header: getHeaderValueForColumn(cell.col),
      class1: criteria.class1,
      l1: criteria.l1,
      l2: criteria.l2,
    });
    if (!ledgerName) {
      console.debug("[TB Drilldown] Missing L2 ledger name");
      return;
    }

    const headerValue = getHeaderValueForColumn(cell.col);
    if (!headerValue) {
      console.debug("[TB Drilldown] Missing header for column", cell.col);
      return;
    }

    const period = parseMonthYearFromHeader(headerValue);
    if (!period) {
      console.debug("[TB Drilldown] Failed to parse period from header", headerValue);
      return;
    }

    const companyId = getStoredCompanyId();
    setTrialBalanceModal({
      isOpen: true,
      ledgerName,
      class1: criteria.class1?.trim(),
      year: period.year,
      monthNumber: period.monthNumber,
      headerLabel: headerValue,
    });
    setTrialBalanceLoading(true);
    setTrialBalanceError("");
    setTrialBalanceTransactions([]);
    setTrialBalanceOpeningBalance(null);
    setTrialBalanceClosingBalance(null);

    try {
      console.debug("[TB Drilldown] Fetching transactions", {
        ledger_name: ledgerName,
        formula: cell.formula,
        class_1: criteria.class1?.trim(),
        year: period.year,
        month_number: period.monthNumber,
        company_id: companyId,
      });
      const response = await getLedgerTransactions({
        ledger_name: ledgerName,
        formula: cell.formula ?? undefined,
        year: period.year,
        month_number: period.monthNumber,
        class_1: criteria.class1?.trim(),
        company_id: companyId,
      });
      setTrialBalanceTransactions(response.data.transactions || []);
      setTrialBalanceOpeningBalance(
        response.data.opening_balance ?? null
      );
      setTrialBalanceClosingBalance(
        response.data.closing_balance ?? null
      );
    } catch (err: any) {
      console.debug("[TB Drilldown] Fetch error", err);
      setTrialBalanceError(err.message || "Failed to load transactions.");
    } finally {
      setTrialBalanceLoading(false);
    }
  };

  // Format report name - remove "Databook - " prefix and sheet suffix
  const formatReportName = useCallback((name: string): string => {
    if (!name) return "";
    // Remove "Databook - " or "Databook-" prefix if present (case insensitive)
    let formatted = name.replace(/^Databook\s*-\s*/i, "");
    // Remove " (IS)" or " (BS)" or " (is)" or " (bs)" suffix if present
    formatted = formatted.replace(/\s*\([Ii][Ss]\)$/i, "").replace(/\s*\([Bb][Ss]\)$/i, "");
    return formatted.trim() || name; // Fallback to original if empty after formatting
  }, []);

  // Handle cell click
  const handleCellClick = useCallback(
    (cell: ExcelCell) => {
      console.log("[TB Drilldown] Cell click", {
        sheet: activeSheet,
        cell: `${cell.col}${cell.row}`,
        value: cell.value,
        formula: cell.formula,
      });
      // Update selected cell
      setSelectedCell({
        sheet: activeSheet,
        row: cell.row,
        col: cell.col,
        value: cell.value,
        formula: cell.formula,
      });

      // If cell has a sheet reference, open source modal
      if (cell.formula && excelData) {
        const trialBalanceCriteria = parseTrialBalanceCriteria(cell.formula);
        if (trialBalanceCriteria) {
          console.debug("[TB Drilldown] Cell formula", cell.formula);
          const headerValue = getHeaderValueForColumn(cell.col);
          const periodInfo =
            headerValue && trialBalanceCriteria
              ? parseMonthYearFromHeader(headerValue)
              : null;
          console.debug("[TB Drilldown] Criteria/header", {
            criteria: trialBalanceCriteria,
            headerValue,
            periodInfo,
          });
          if (trialBalanceCriteria.l2 && periodInfo) {
            handleTrialBalanceDrilldown(cell, trialBalanceCriteria);
            return;
          }
        }
        const refs = getAllSheetReferences(cell.formula);
        // Find the first reference that points to an existing sheet
        const ref = refs.find((r) => excelData.sheets[r.sheet]);
        if (ref) {
          const fromCell = `${cell.col}${cell.row}`;
          setBreadcrumbs((prev) => [...prev, { sheet: activeSheet, cell: fromCell }]);
          setActiveSheet(ref.sheet);
          setTargetCellAddress(ref.cell);
        }
      }
    },
    [activeSheet, currentSheet, excelData, filteredSheet]
  );

  // Format cell value for display
  const formatCellValue = (cell: ExcelCell): string => {
    const value = cell.value;
    if (value === null || value === undefined || value === "") {
      return "";
    }
    // Use number format from style if available
    return formatWithNumberFormat(value, cell.style?.numberFormat);
  };

  // Check if a cell is selected
  const isCellSelected = (cell: ExcelCell): boolean => {
    return (
      selectedCell !== null &&
      selectedCell.row === cell.row &&
      selectedCell.col === cell.col &&
      selectedCell.sheet === activeSheet
    );
  };

  const hasSheetReference = (cell: ExcelCell): boolean => {
    if (!cell.formula || !excelData) return false;
    const refs = getAllSheetReferences(cell.formula);
    return refs.some((ref) => !!excelData.sheets[ref.sheet]);
  };

  const handleBreadcrumbClick = (index: number) => {
    const crumb = breadcrumbs[index];
    if (!crumb) return;
    setActiveSheet(crumb.sheet);
    setTargetCellAddress(crumb.cell);
    setBreadcrumbs((prev) => prev.slice(0, index));
  };

  const getEffectiveCellStyle = (
    cell: ExcelCell,
    mergeInfo?: MergedCellInfo
  ): React.CSSProperties => {
    const style = { ...cellStyleToCSS(cell.style) };

    if (mergeInfo?.masterCell) {
      const master = parseAddress(mergeInfo.masterCell);
      if (master) {
        const startColIndex = columnToIndex(master.col);
        const endColIndex = startColIndex + (mergeInfo.colSpan || 1) - 1;
        const startRow = master.row;
        const endRow = startRow + (mergeInfo.rowSpan || 1) - 1;

        const findBorderFromEdge = (
          side: "top" | "bottom" | "left" | "right"
        ): string | undefined => {
          if (side === "top" || side === "bottom") {
            const edgeRow = side === "top" ? startRow : endRow;
            for (let colIndex = startColIndex; colIndex <= endColIndex; colIndex++) {
              const edgeCell = filteredCellMap.get(`${indexToColumn(colIndex)}${edgeRow}`);
              const cssBorder = borderToCss(edgeCell?.style?.borders, side);
              if (cssBorder) return cssBorder;
            }
            return undefined;
          }

          const edgeCol = side === "left" ? startColIndex : endColIndex;
          for (let rowNum = startRow; rowNum <= endRow; rowNum++) {
            const edgeCell = filteredCellMap.get(`${indexToColumn(edgeCol)}${rowNum}`);
            const cssBorder = borderToCss(edgeCell?.style?.borders, side);
            if (cssBorder) return cssBorder;
          }
          return undefined;
        };

        if (!style.borderTop) {
          const mergedTop = findBorderFromEdge("top");
          if (mergedTop) style.borderTop = mergedTop;
        }
        if (!style.borderBottom) {
          const mergedBottom = findBorderFromEdge("bottom");
          if (mergedBottom) style.borderBottom = mergedBottom;
        }
        if (!style.borderLeft) {
          const mergedLeft = findBorderFromEdge("left");
          if (mergedLeft) style.borderLeft = mergedLeft;
        }
        if (!style.borderRight) {
          const mergedRight = findBorderFromEdge("right");
          if (mergedRight) style.borderRight = mergedRight;
        }
      }
    }

    // No fallback grid line: only show borders that come from the Excel style (thick/thin).

    return style;
  };

  useEffect(() => {
    if (!targetCellAddress) return;
    const target = gridRef.current?.querySelector(
      `[data-cell-address="${targetCellAddress}"]`
    ) as HTMLTableCellElement | null;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      }, 50);
    }
    const parsed = parseAddress(targetCellAddress);
    if (parsed) {
      setSelectedCell({
        sheet: activeSheet,
        row: parsed.row,
        col: parsed.col,
        value: null,
        formula: null,
      });
    }
  }, [targetCellAddress, activeSheet, filteredSheet?.rows.length]);

  // Handle download
  const handleDownload = () => {
    if (reportId && excelData) {
      downloadReport(reportId, excelData.fileName);
      if (onDownload) {
        onDownload();
      }
    }
  };

  const headerTitle = useMemo(() => {
    if (headerAsOfLabel) {
      const base = (fallbackTitle ?? "").trim();
      return base.length > 0
        ? `${base} — As of ${headerAsOfLabel}`
        : `As of ${headerAsOfLabel}`;
    }
    return reportName ? formatReportName(reportName) : fallbackTitle || "";
  }, [headerAsOfLabel, fallbackTitle, reportName, formatReportName]);

  if (!reportId && !showHeaderWhenEmpty) {
    return null;
  }

  const showLoading = loading || isGenerating;

  const showTransactionsInline = !!trialBalanceModal;

  return (
    <div className="flex flex-col w-full h-full bg-default-background">
      {/* Header with Download */}
      {(reportId || showHeaderWhenEmpty) && (
        <div className="flex flex-col gap-2 px-4 py-3 border-b border-neutral-border bg-neutral-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-heading-3 font-heading-3 text-default-font">
                {headerTitle}
              </h2>
              {showTransactionsInline && (
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={() => {
                    setTrialBalanceModal(null);
                    setTrialBalanceTransactions([]);
                    setTrialBalanceError("");
                    setTrialBalanceOpeningBalance(null);
                    setTrialBalanceClosingBalance(null);
                  }}
                >
                  Back to sheet
                </Button>
              )}
            </div>
            <div className="flex items-end gap-2">
              {headerActions}
              {excelData && (
                <Button
                  variant="neutral-tertiary"
                  icon={<FeatherDownload />}
                  size="small"
                  onClick={handleDownload}
                >
                  Download Excel
                </Button>
              )}
            </div>
          </div>
          {!showTransactionsInline && breadcrumbs.length > 0 && (
            <div className="flex items-center gap-2 text-sm font-body text-subtext-color">
              <span className="text-subtext-color">Drilldown:</span>
              {breadcrumbs.map((crumb, index) => (
                <button
                  key={`${crumb.sheet}-${crumb.cell}-${index}`}
                  onClick={() => handleBreadcrumbClick(index)}
                  className="text-brand-600 hover:text-brand-800 underline"
                  title={`Go back to ${crumb.sheet} (${crumb.cell})`}
                >
                  {crumb.sheet} ({crumb.cell})
                </button>
              ))}
              <span className="text-subtext-color">/ {activeSheet}</span>
            </div>
          )}
        </div>
      )}

      {/* Spreadsheet Grid */}
      <div
        ref={gridRef}
        className="flex-1 overflow-auto bg-white transition-opacity duration-200"
        style={{ overflowX: "auto", overflowY: "auto" }}
      >
        {showTransactionsInline ? (
          <div className="flex-1 overflow-auto bg-white">
            {trialBalanceLoading ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-subtext-color">Loading transactions...</span>
              </div>
            ) : trialBalanceError ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-error-600">{trialBalanceError}</span>
              </div>
            ) : trialBalanceTransactions.length === 0 &&
              trialBalanceOpeningBalance === null &&
              trialBalanceClosingBalance === null ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-subtext-color">No transactions found.</span>
              </div>
            ) : (
              <table className="border-collapse min-w-full">
                <thead className="sticky top-0 z-20">
                  <tr>
                    <th className="px-3 py-2 text-left text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                      Date
                    </th>
                    <th className="px-3 py-2 text-left text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                      Type
                    </th>
                    <th className="px-3 py-2 text-left text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                      Reference
                    </th>
                    <th className="px-3 py-2 text-left text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                      Payee
                    </th>
                    <th className="px-3 py-2 text-right text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                      Debit
                    </th>
                    <th className="px-3 py-2 text-right text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                      Credit
                    </th>
                    <th className="px-3 py-2 text-center text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                      View
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {trialBalanceOpeningBalance !== null && (
                    <tr className="bg-neutral-50">
                      <td className="px-3 py-2 text-body font-body border border-neutral-200 font-semibold">
                        Opening Balance
                      </td>
                      <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                      <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                      <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                      <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                        {trialBalanceOpeningBalance >= 0
                          ? new Intl.NumberFormat("en-IN").format(trialBalanceOpeningBalance)
                          : "-"}
                      </td>
                      <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                        {trialBalanceOpeningBalance < 0
                          ? new Intl.NumberFormat("en-IN").format(
                              Math.abs(trialBalanceOpeningBalance)
                            )
                          : "-"}
                      </td>
                      <td className="px-3 py-2 text-body font-body border border-neutral-200 text-center">
                        -
                      </td>
                    </tr>
                  )}
                  {trialBalanceTransactions.map((tx) => {
                    const viewId = tx.view_document_id;
                    const canView = !!viewId;
                    const journalRef = tx.reference_number || tx.entry_number || null;
                    const transactionType = (tx.transaction_type || "").toLowerCase();
                    const viewUrl = viewId
                      ? `${window.location.origin}/${
                          tx.view_document_type === "invoice" ? "invoices" : "bills"
                        }/review?documentId=${viewId}`
                      : transactionType === "journal" && journalRef
                      ? `${window.location.origin}/journal/detail/${journalRef}`
                      : null;
                    return (
                      <tr key={tx.id} className="hover:bg-neutral-50">
                        <td className="px-3 py-2 text-body font-body border border-neutral-200">
                          {tx.transaction_date || "-"}
                        </td>
                        <td className="px-3 py-2 text-body font-body border border-neutral-200">
                          {tx.transaction_type || "-"}
                        </td>
                        <td className="px-3 py-2 text-body font-body border border-neutral-200">
                          {tx.reference_number || tx.entry_number || "-"}
                        </td>
                        <td className="px-3 py-2 text-body font-body border border-neutral-200">
                          {tx.payee || "-"}
                        </td>
                        <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                          {tx.debit_amount === null || tx.debit_amount === undefined
                            ? "-"
                            : new Intl.NumberFormat("en-IN").format(tx.debit_amount)}
                        </td>
                        <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                          {tx.credit_amount === null || tx.credit_amount === undefined
                            ? "-"
                            : new Intl.NumberFormat("en-IN").format(tx.credit_amount)}
                        </td>
                        <td className="px-3 py-2 text-body font-body border border-neutral-200 text-center">
                          {viewUrl ? (
                            <Button
                              variant="neutral-tertiary"
                              size="small"
                              onClick={() => window.open(viewUrl, "_blank")}
                            >
                              View
                            </Button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {trialBalanceClosingBalance !== null && (
                    <tr className="bg-neutral-50">
                      <td className="px-3 py-2 text-body font-body border border-neutral-200 font-semibold">
                        Closing Balance
                      </td>
                      <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                      <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                      <td className="px-3 py-2 text-body font-body border border-neutral-200" />
                      <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                        {trialBalanceClosingBalance >= 0
                          ? new Intl.NumberFormat("en-IN").format(trialBalanceClosingBalance)
                          : "-"}
                      </td>
                      <td className="px-3 py-2 text-body font-body border border-neutral-200 text-right">
                        {trialBalanceClosingBalance < 0
                          ? new Intl.NumberFormat("en-IN").format(
                              Math.abs(trialBalanceClosingBalance)
                            )
                          : "-"}
                      </td>
                      <td className="px-3 py-2 text-body font-body border border-neutral-200 text-center">
                        -
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        ) : showLoading ? (
          <div className="flex items-center justify-center h-full animate-pulse">
            <div className="flex items-center gap-3 text-subtext-color">
              <FeatherLoader className="animate-spin" />
              <span>{loading ? "Loading spreadsheet..." : "Generating report..."}</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-error-600">{error}</div>
          </div>
        ) : filteredSheet ? (
          <table className="border-separate border-spacing-0 min-w-full">
            {/* Column widths */}
            {filteredSheet.columnWidths && (
              <colgroup>
                {filteredSheet.rows[0]?.map((cell) => (
                  <col
                    key={cell.col}
                    style={{
                      width: filteredSheet.columnWidths?.[cell.col]
                        ? `${filteredSheet.columnWidths[cell.col]}px`
                        : "120px",
                      minWidth: "60px",
                    }}
                  />
                ))}
              </colgroup>
            )}
            <tbody>
              {filteredSheet.rows.map((row, rowIndex) => {
                const rowNum = rowIndex + 1;
                const rowHeight = filteredSheet.rowHeights?.[rowNum];
                return (
                  <tr
                    key={rowIndex}
                    style={rowHeight ? { height: `${rowHeight}px` } : undefined}
                  >
                    {row.map((cell) => {
                      const cellAddress = `${cell.col}${cell.row}`;
                      const mergeInfo = mergedCellsMap.get(cellAddress);

                      // Skip hidden cells (covered by merge)
                      if (mergeInfo?.isHidden) {
                        return null;
                      }

                      // Get cell style, then extend row borders so lines run across full row (e.g. under Notes column)
                      let cellStyle = getEffectiveCellStyle(cell, mergeInfo);
                      if (!cellStyle.borderBottom) {
                        const rowBottom = getRowBorderForSide(row, "bottom");
                        if (rowBottom) cellStyle = { ...cellStyle, borderBottom: rowBottom };
                      }
                      if (!cellStyle.borderTop) {
                        const rowTop = getRowBorderForSide(row, "top");
                        if (rowTop) cellStyle = { ...cellStyle, borderTop: rowTop };
                      }

                      const isDrilldownCell = hasSheetReference(cell);
                      const trialBalanceCriteria = parseTrialBalanceCriteria(cell.formula);
                      const headerValue = getHeaderValueForColumn(cell.col);
                      const periodInfo =
                        headerValue && trialBalanceCriteria
                          ? parseMonthYearFromHeader(headerValue)
                          : null;
                      const isTrialBalanceCell =
                        !!trialBalanceCriteria?.l2 && !!periodInfo;

                      return (
                        <td
                          key={cellAddress}
                          onClick={() => {
                            console.log("[TB Drilldown] TD clicked", {
                              sheet: activeSheet,
                              cell: cellAddress,
                            });
                            handleCellClick(cell);
                          }}
                          colSpan={mergeInfo?.colSpan || 1}
                          rowSpan={mergeInfo?.rowSpan || 1}
                          data-cell-address={cellAddress}
                          className={`px-3 py-1 text-body font-body max-w-[300px] truncate cursor-pointer transition-colors ${
                            isCellSelected(cell)
                              ? "bg-brand-100 border-brand-500 border-2"
                                : isTrialBalanceCell && !cell.style?.backgroundColor
                                ? "bg-purple-50 hover:bg-purple-100"
                              : isDrilldownCell && !cell.style?.backgroundColor
                              ? "bg-blue-50 hover:bg-blue-100"
                              : ""
                          }`}
                          style={cellStyle}
                          title={
                            cell.formula
                              ? `Formula: ${cell.formula}\nValue: ${cell.value}`
                              : String(cell.value || "")
                          }
                        >
                          {formatCellValue(cell)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-subtext-color">
              {emptyState || "No data available"}
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      {showStatusBar && (
        <div className="flex items-center justify-between px-6 py-2 border-t border-neutral-border bg-neutral-50">
          <span className="text-caption font-caption text-subtext-color">
            {filteredSheet
              ? `${filteredSheet.rows.length} rows × ${filteredSheet.rows[0]?.length || 0} columns`
              : ""}
          </span>
        </div>
      )}

    </div>
  );
}
